import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Loader2, ChevronDown, ChevronUp, ExternalLink, GitMerge, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { productsAPI } from '../services/api';
import { productPath } from '../services/productPath';
import { toast } from 'sonner';

const REASONS = { fuels: 'Carburants incompatibles', brands: 'Aucune marque de véhicule en commun' };

const Side = ({ p, detail, onEdit }) => (
  <div className="min-w-0">
    <button type="button" onClick={() => onEdit?.(p.id)} className="text-sm font-medium text-gray-900 hover:text-orange-600 text-left truncate block max-w-xs" data-testid={`compat-conflict-edit-${p.id}`}>{p.name}</button>
    <p className="text-xs text-gray-500 truncate">{p.brand} · {p.ref} <Link to={productPath(p)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5 ml-1"><ExternalLink size={10} /> fiche</Link></p>
    <p className="text-xs text-gray-600 truncate" title={(detail || []).join(', ')}>{(detail || []).join(', ')}</p>
  </div>
);

const ConflictRow = ({ c, index, onEdit, onDone }) => {
  const [busy, setBusy] = useState(null);
  const act = async (action) => {
    setBusy(action);
    try {
      await productsAPI.compatConflictAction(c.a.id, c.b.id, action);
      toast.success(action === 'merge' ? 'Compatibilités fusionnées malgré la contradiction' : 'Les deux fiches restent indépendantes');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setBusy(null);
  };
  return (
    <li className="bg-white border border-red-100 rounded-lg p-3 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto] gap-3 items-center" data-testid={`compat-conflict-${index}`}>
      <Side p={c.a} detail={c.detail?.a} onEdit={onEdit} />
      <span className="text-[11px] font-semibold text-red-700 bg-red-100 px-2 py-1 rounded-full text-center whitespace-nowrap" data-testid={`compat-conflict-reason-${index}`}>{REASONS[c.reason] || c.reason}</span>
      <Side p={c.b} detail={c.detail?.b} onEdit={onEdit} />
      <div className="flex flex-col gap-1.5">
        <Button size="sm" variant="outline" onClick={() => act('merge')} disabled={!!busy} data-testid={`compat-conflict-merge-${index}`} title="Ignorer la contradiction et hériter quand même" className="text-xs">{busy === 'merge' ? <Loader2 size={12} className="animate-spin" /> : <GitMerge size={12} className="mr-1" />} Fusionner quand même</Button>
        <Button size="sm" variant="outline" onClick={() => act('block')} disabled={!!busy} data-testid={`compat-conflict-block-${index}`} title="Ne jamais hériter entre ces deux fiches" className="text-xs border-red-300 text-red-700 hover:bg-red-50">{busy === 'block' ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} className="mr-1" />} Garder séparées</Button>
      </div>
    </li>
  );
};

// Admin › Produits : equivalent parts whose vehicle compatibilities contradict each other (held back from auto-merge)
const CompatConflictsCard = ({ refreshKey, onEdit, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await productsAPI.compatConflicts()); } catch { setData({ items: [] }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return null;
  const items = data.items || [];
  return (
    <div className={`rounded-lg shadow-sm p-4 mb-6 ${items.length ? 'bg-red-50 border border-red-200' : 'bg-white'}`} data-testid="compat-conflicts-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className={`font-semibold flex items-center gap-2 ${items.length ? 'text-red-800' : 'text-gray-900'}`}><AlertTriangle size={16} className={items.length ? 'text-red-600' : 'text-gray-400'} /> Incohérences entre pièces équivalentes</h3>
          <p className="text-xs text-gray-600" data-testid="compat-conflicts-summary">
            {items.length ? `${items.length} paire(s) d'équivalences aux compatibilités contradictoires (carburants ou marques de véhicules sans point commun) : l'héritage est suspendu entre elles jusqu'à votre décision.` : 'Aucune contradiction détectée entre les compatibilités des pièces équivalentes.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} disabled={loading} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1" data-testid="compat-conflicts-refresh">{loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} actualiser</button>
          {items.length > 0 && <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-0.5" data-testid="compat-conflicts-toggle">{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} détails</button>}
        </div>
      </div>
      {open && items.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="compat-conflicts-list">
          {items.map((c, i) => <ConflictRow key={`${c.a.id}-${c.b.id}`} c={c} index={i} onEdit={onEdit} onDone={() => { load(); onChanged?.(); }} />)}
        </ul>
      )}
    </div>
  );
};

export default CompatConflictsCard;
